import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateCut, setChangedRowCutList } from '@modules/editor';
import { milliSecondsToFormattedTime } from '@utils/milliSecondsToFormattedTime';
import { toast, Flip } from 'react-toastify';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

function CutRow({ idx, el, setChangingList, videoDurationTime }) {

    const dispatch = useDispatch();
    const changedTimelineCutList = useSelector((state: any) => state.editor.changedTimelineCutList);
    const cutList = useSelector((state: any) => state.editor.cutList);
    const { t } = useTranslation();

    const [cut, setCut] = useState({ from: el.from, to: el.to });
    const [colors, setColors] = useState<Colors>({ from: 'black', to: 'black' });
    const fromRef = useRef<HTMLDivElement>(null);
    const toRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (changedTimelineCutList && Object.keys(changedTimelineCutList).length > 0) {
            const { from, to } = changedTimelineCutList;
            if (changedTimelineCutList.regionIndex === idx) {
                setCut({ from: from, to: to });
                updateInputValue('from', from);
                updateInputValue('to', to);
                setColors({ from: 'black', to: 'black' });
            }
        }
    }, [changedTimelineCutList]);

    useEffect(() => {
        if (cutList.length > 0) {
            setCut({ from: cutList[idx].from, to: cutList[idx].to });
            updateInputValue('from', cutList[idx].from);
            updateInputValue('to', cutList[idx].to);
        }
    }, [cutList]);

    const updateInputValue = useCallback((type: 'from' | 'to', value: number) => {
        const ref = type === 'from' ? fromRef : toRef;
        if (ref.current) {
            ref.current.textContent = milliSecondsToFormattedTime(value);
        }
    }, []);

    const validateTime = useCallback((input: string): boolean => {
        const regex = /^(\d{2}):(\d{2}):(\d{1,2})(\.\d{1,3})?$/;
        const match = input.match(regex);

        if (!match) return false;

        const [_, hours, minutes, seconds] = match;
        if (parseInt(hours) > 23 || parseInt(minutes) > 59 || parseInt(seconds) > 59) {
            return false;
        }

        return true;
    }, []);

    const changeTime = useCallback((e: React.FormEvent<HTMLDivElement>, type: 'from' | 'to') => {
        dispatch(setChangedRowCutList({ ...el, type: type, regionIndex: idx }));
        const newValue = (e.currentTarget as HTMLDivElement).textContent || '';
        if (!validateTime(newValue)) {
            setColors(prev => ({ ...prev, [type]: 'red' }));
        } else {
            setColors(prev => ({ ...prev, [type]: 'green' }));
        }
        return newValue;
    }, [validateTime, el, idx, dispatch, setChangingList]);

    function handleBlur(type: 'from' | 'to', newValue: string) {
        setColors({ from: 'black', to: 'black' });
        dispatch(setChangedRowCutList({}));

        const [hours, minutes, seconds, milliseconds = '000'] = newValue.split(/[:\.]/);
        const totalSeconds = (parseInt(hours) * 3600 +
            parseInt(minutes) * 60 +
            parseInt(seconds.padStart(2, '0'))) +
            parseInt(milliseconds.padEnd(3, '0')) / 1000;

        const newCut = {
            ...cut,
            [type === 'from' ? 'from' : 'to']: totalSeconds
        };

        const newStart = type === 'from' ? totalSeconds : cut.from;
        const newEnd = type === 'to' ? totalSeconds : cut.to;
        console.log('newStart', newStart);
        console.log('newEnd', newEnd);
        console.log('cutList[idx - 1].to', cutList);

        if (type === 'to' && typeof videoDurationTime === 'number' && newEnd > videoDurationTime) {
            toast.error(t('vod.manage.editor_tab.end_time_exceeds_video_length'), {
                position: 'top-center',
                transition: Flip
            });
            newCut.to = cutList[idx].to;
        }
        else if (newStart > newEnd) {
            toast.error(t("vod.manage.editor_tab.start_time_must_be_earlier"), {
                position: "top-center",
                transition: Flip
            });
            newCut.from = cutList[idx].from;
            newCut.to = cutList[idx].to;
            console.log('newCut', newCut);
        }
        else if (newStart == newEnd) {
            toast.error(t("vod.manage.editor_tab.start_end_time_cannot_be_same"), {
                position: "top-center",
                transition: Flip
            });
            newCut.from = cutList[idx].from;
            newCut.to = cutList[idx].to;
        }

        if (idx > 0 && newStart < cutList[idx - 1].to) {
            toast.error("이전 컷의 종료 시간보다 빨라질 수 없습니다", {
                position: "top-center",
                transition: Flip
            });
            newCut.from = cutList[idx - 1].to;
        }

        if (idx < cutList.length - 1 && newEnd > cutList[idx + 1].from) {
            toast.error("다음 컷의 시작 시간보다 늦어질 수 없습니다", {
                position: "top-center",
                transition: Flip
            });
            newCut.to = cutList[idx + 1].from;
        }

        setCut(newCut);
        dispatch(updateCut({ ...newCut, regionIndex: idx }));

        updateInputValue('from', newCut.from);
        updateInputValue('to', newCut.to);
    }

    return (
        <Box className='row-editable-time'>
            <div
                ref={fromRef}
                className='editor editable-text'
                contentEditable="true"
                onInput={e => changeTime(e, 'from')}
                onBlur={() => handleBlur('from', fromRef.current?.textContent || '')}
                suppressContentEditableWarning={true}
                style={{ color: colors.from, marginRight: '10px' }}
            >
                {milliSecondsToFormattedTime(cut.from)}
            </div>
            <Typography style={{ marginRight: '10px' }}> - </Typography>
            <div
                ref={toRef}
                className='editor editable-text'
                contentEditable="true"
                onInput={e => changeTime(e, 'to')}
                onBlur={() => handleBlur('to', toRef.current?.textContent || '')}
                suppressContentEditableWarning={true}
                style={{ color: colors.to }}
            >
                {milliSecondsToFormattedTime(cut.to)}
            </div>
        </Box>
    );
}

export default CutRow;